(() => {
  'use strict';

  /*
   * SAM – One Stop Volume Discounts
   * Selector visual estilo competencia:
   *
   * [imagen] [Color ▼] [Talla ▼]
   *
   * IMPORTANTE:
   * - El <select> original de One Stop sigue existiendo.
   * - Los selectores nuevos solo controlan visualmente el original.
   * - One Stop sigue recibiendo el variant ID correcto.
   */

  const CONFIG = {
    selector: 'select.os-vd__tier-variant-selector',
    processedClass: 'sam-competitor-variant-processed',
    wrapperClass: 'sam-competitor-split-selectors'
  };

  let processing = false;
  let scheduled = false;

  /* =========================================================
     HELPERS
  ========================================================= */

  function normalizeText(value) {
    return String(value || '').trim();
  }

  function parseVariantText(text) {
    const parts = normalizeText(text)
      .split('/')
      .map(part => part.trim());

    return {
      color: parts[0] || '',
      size: parts.slice(1).join(' / ') || ''
    };
  }

  function unique(values) {
    return [...new Set(values.filter(Boolean))];
  }

  function escapeSelectorValue(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(String(value));
    }

    return String(value).replace(/["\\]/g, '\\$&');
  }

  function dispatchNativeChange(select) {
    select.dispatchEvent(
      new Event('change', {
        bubbles: true
      })
    );

    select.dispatchEvent(
      new Event('input', {
        bubbles: true
      })
    );
  }

  function getVariantData(originalSelect) {
    return Array.from(originalSelect.options).map(option => {
      const parsed = parseVariantText(option.textContent);

      return {
        id: option.value,
        color: parsed.color,
        size: parsed.size,
        disabled: option.disabled,
        text: normalizeText(option.textContent)
      };
    });
  }

  function getCurrentVariant(originalSelect, variants) {
    const currentValue = String(originalSelect.value);

    return (
      variants.find(variant => String(variant.id) === currentValue) ||
      variants[0] ||
      null
    );
  }

  function findVariant(variants, color, size) {
    return variants.find(
      variant =>
        variant.color === color &&
        variant.size === size &&
        !variant.disabled
    );
  }

  function getAvailableSizes(variants, color) {
    return unique(
      variants
        .filter(variant => variant.color === color && !variant.disabled)
        .map(variant => variant.size)
    );
  }

  function getAvailableColors(variants) {
    return unique(
      variants
        .filter(variant => !variant.disabled)
        .map(variant => variant.color)
    );
  }

  /* =========================================================
     CREATE VISUAL SELECT
  ========================================================= */

  function createSelect(className, ariaLabel) {
    const select = document.createElement('select');

    select.className = className;
    select.setAttribute('aria-label', ariaLabel);

    return select;
  }

  function createOption(value, label) {
    const option = document.createElement('option');

    option.value = value;
    option.textContent = label;

    return option;
  }

  function populateColorSelect(colorSelect, colors, selectedColor) {
    colorSelect.innerHTML = '';

    colors.forEach(color => {
      const option = createOption(color, color);

      if (color === selectedColor) {
        option.selected = true;
      }

      colorSelect.appendChild(option);
    });
  }

  function populateSizeSelect(
    sizeSelect,
    variants,
    selectedColor,
    selectedSize
  ) {
    sizeSelect.innerHTML = '';

    const sizes = getAvailableSizes(variants, selectedColor);

    sizes.forEach(size => {
      const option = createOption(size, size);

      if (size === selectedSize) {
        option.selected = true;
      }

      sizeSelect.appendChild(option);
    });

    if (!sizes.includes(selectedSize) && sizes.length) {
      sizeSelect.value = sizes[0];
    }
  }

  /* =========================================================
     SYNC NEW SELECTS -> ORIGINAL ONE STOP SELECT
  ========================================================= */

  function syncOriginalVariant(
    originalSelect,
    variants,
    colorSelect,
    sizeSelect
  ) {
    let color = colorSelect.value;
    let size = sizeSelect.value;

    let variant = findVariant(variants, color, size);

    /*
     * Si la combinación elegida no existe,
     * buscamos la primera talla disponible para ese color.
     */
    if (!variant) {
      const availableSizes = getAvailableSizes(variants, color);

      if (availableSizes.length) {
        size = availableSizes[0];
        sizeSelect.value = size;

        variant = findVariant(variants, color, size);
      }
    }

    if (!variant) return;

    if (String(originalSelect.value) !== String(variant.id)) {
      originalSelect.value = variant.id;
      dispatchNativeChange(originalSelect);
    }
  }

  /* =========================================================
     SYNC ORIGINAL ONE STOP SELECT -> NEW SELECTS
  ========================================================= */

  function syncVisualSelectors(
    originalSelect,
    variants,
    colorSelect,
    sizeSelect
  ) {
    const current = getCurrentVariant(originalSelect, variants);

    if (!current) return;

    if (colorSelect.value !== current.color) {
      colorSelect.value = current.color;
    }

    populateSizeSelect(
      sizeSelect,
      variants,
      current.color,
      current.size
    );

    sizeSelect.value = current.size;
  }

  /* =========================================================
     BUILD SPLIT SELECTORS
  ========================================================= */

  function transformSelect(originalSelect) {
    if (!(originalSelect instanceof HTMLSelectElement)) return;

    /*
     * Si One Stop recreó el select, lo procesamos.
     * Si ya tiene nuestro wrapper válido, no hacemos nada.
     */
    const existingWrapper = originalSelect.parentElement?.querySelector(
      `.${CONFIG.wrapperClass}`
    );

    if (
      originalSelect.dataset.samSplitReady === 'true' &&
      existingWrapper
    ) {
      return;
    }

    const variants = getVariantData(originalSelect);

    if (!variants.length) return;

    const current = getCurrentVariant(originalSelect, variants);

    if (!current) return;

    const colors = getAvailableColors(variants);

    if (!colors.length) return;

    /*
     * Wrapper de los dos selectores.
     */
    const wrapper = document.createElement('div');
    wrapper.className = CONFIG.wrapperClass;

    /*
     * Selector de color.
     */
    const colorSelect = createSelect(
      'sam-competitor-color-select',
      'Color'
    );

    /*
     * Selector de talla.
     */
    const sizeSelect = createSelect(
      'sam-competitor-size-select',
      'Talla'
    );

    populateColorSelect(
      colorSelect,
      colors,
      current.color
    );

    populateSizeSelect(
      sizeSelect,
      variants,
      current.color,
      current.size
    );

    colorSelect.value = current.color;
    sizeSelect.value = current.size;

    wrapper.appendChild(colorSelect);
    wrapper.appendChild(sizeSelect);

    /*
     * Insertamos los selectores visuales justo después
     * del select original de One Stop.
     */
    originalSelect.insertAdjacentElement('afterend', wrapper);

    /*
     * Ocultamos SOLO visualmente el selector original.
     * No usamos display:none para reducir el riesgo de
     * interferir con la lógica interna de la app.
     */
    originalSelect.classList.add(CONFIG.processedClass);
    originalSelect.dataset.samSplitReady = 'true';

    /*
     * COLOR
     */
    colorSelect.addEventListener('change', () => {
      const selectedColor = colorSelect.value;

      const previousSize = sizeSelect.value;

      populateSizeSelect(
        sizeSelect,
        variants,
        selectedColor,
        previousSize
      );

      syncOriginalVariant(
        originalSelect,
        variants,
        colorSelect,
        sizeSelect
      );
    });

    /*
     * TALLA
     */
    sizeSelect.addEventListener('change', () => {
      syncOriginalVariant(
        originalSelect,
        variants,
        colorSelect,
        sizeSelect
      );
    });

    /*
     * Si One Stop cambia el select original internamente,
     * sincronizamos nuestra interfaz.
     */
    originalSelect.addEventListener('change', () => {
      syncVisualSelectors(
        originalSelect,
        variants,
        colorSelect,
        sizeSelect
      );
    });
  }

  /* =========================================================
     CLEAN DUPLICATE WRAPPERS
  ========================================================= */

  function cleanDuplicateWrappers() {
    document
      .querySelectorAll(CONFIG.selector)
      .forEach(originalSelect => {
        const parent = originalSelect.parentElement;

        if (!parent) return;

        const wrappers = Array.from(
          parent.querySelectorAll(`.${CONFIG.wrapperClass}`)
        );

        if (wrappers.length <= 1) return;

        wrappers.slice(1).forEach(wrapper => wrapper.remove());
      });
  }

  /* =========================================================
     REMOVE #1 / #2 / #3
  ========================================================= */

  function hideVariantNumbers() {
    /*
     * One Stop utiliza este elemento para #1, #2, #3.
     */
    document
      .querySelectorAll('.os-vd__tier-variant-number')
      .forEach(element => {
        element.style.setProperty('display', 'none', 'important');
      });
  }

  /* =========================================================
     PROCESS ALL
  ========================================================= */

  function processAll() {
    if (processing) return;

    processing = true;

    try {
      const selects = document.querySelectorAll(CONFIG.selector);

      selects.forEach(transformSelect);

      cleanDuplicateWrappers();
      hideVariantNumbers();
    } finally {
      processing = false;
    }
  }

  function scheduleProcess() {
    if (scheduled) return;

    scheduled = true;

    requestAnimationFrame(() => {
      scheduled = false;
      processAll();
    });
  }

  /* =========================================================
     CSS
  ========================================================= */

  function injectStyles() {
    if (document.getElementById('sam-competitor-variants-css')) {
      return;
    }

    const style = document.createElement('style');

    style.id = 'sam-competitor-variants-css';

    style.textContent = `
      /*
       * =====================================================
       * SAM — VARIANT SELECTORS
       * Replica visual del selector de la competencia
       * =====================================================
       */

      /* Ocultar visualmente selector combinado original */
      select.os-vd__tier-variant-selector.${CONFIG.processedClass} {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }

      /* Quitar #1, #2, #3 */
      .os-vd__tier-variant-number {
        display: none !important;
      }

      /*
       * Fila principal de cada variante
       */
      .os-vd__tier-variant-content {
        display: flex !important;
        align-items: center !important;
        justify-content: flex-start !important;
        gap: 6px !important;
        width: 100% !important;
        min-width: 0 !important;
      }

      .os-vd__tier-variant-right {
        display: flex !important;
        align-items: center !important;
        justify-content: flex-start !important;
        flex: 0 0 auto !important;
        width: auto !important;
        min-width: 0 !important;
      }

      /*
       * Imagen
       */
      .os-vd__tier-variant-image {
        width: 40px !important;
        height: 40px !important;
        min-width: 40px !important;
        max-width: 40px !important;
        object-fit: contain !important;
        margin: 0 !important;
      }

      /*
       * Wrapper Color + Talla
       */
      .${CONFIG.wrapperClass} {
        display: flex !important;
        flex-direction: row !important;
        align-items: center !important;
        justify-content: flex-start !important;
        gap: 6px !important;
        width: auto !important;
        min-width: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
      }

      /*
       * Base de ambos selects
       */
      .sam-competitor-color-select,
      .sam-competitor-size-select {
        display: block !important;

        height: 32px !important;
        min-height: 32px !important;
        max-height: 32px !important;

        margin: 0 !important;

        border: 1px solid #b7b7b7 !important;
        border-radius: 7px !important;

        background-color: #ffffff !important;

        color: #111111 !important;

        font-family: inherit !important;
        font-size: 14px !important;
        font-weight: 400 !important;
        line-height: 30px !important;

        box-sizing: border-box !important;

        cursor: pointer !important;

        box-shadow: none !important;
        outline: none !important;

        appearance: auto !important;
        -webkit-appearance: menulist !important;

        padding-top: 0 !important;
        padding-bottom: 0 !important;
      }

      /*
       * COLOR
       */
      .sam-competitor-color-select {
        width: 70px !important;
        min-width: 70px !important;
        max-width: 70px !important;

        padding-left: 7px !important;
        padding-right: 2px !important;
      }

      /*
       * TALLA
       */
      .sam-competitor-size-select {
        width: 44px !important;
        min-width: 44px !important;
        max-width: 44px !important;

        padding-left: 7px !important;
        padding-right: 2px !important;
      }

      /*
       * Focus
       */
      .sam-competitor-color-select:focus,
      .sam-competitor-size-select:focus {
        border-color: #111111 !important;
        outline: none !important;
        box-shadow: none !important;
      }

      /*
       * Caja que contiene las filas
       */
      .os-vd__tier-variants {
        margin-top: 8px !important;
      }

      /*
       * Cada fila
       */
      .os-vd__tier-variant {
        display: flex !important;
        align-items: center !important;
        justify-content: flex-start !important;

        min-height: 40px !important;

        margin: 0 0 4px 0 !important;
        padding: 0 !important;

        gap: 6px !important;
      }

      .os-vd__tier-variant:last-child {
        margin-bottom: 0 !important;
      }

      /*
       * Algunos renders de One Stop envuelven la imagen
       * dentro de otros contenedores.
       */
      .os-vd__tier-variant-left {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;

        flex: 0 0 40px !important;
        width: 40px !important;
        min-width: 40px !important;

        margin: 0 !important;
        padding: 0 !important;
      }

      /*
       * Evitar que One Stop estire el selector.
       */
      .os-vd__tier-variant-selector-wrapper {
        width: auto !important;
        min-width: 0 !important;
        flex: 0 0 auto !important;
        margin: 0 !important;
        padding: 0 !important;
      }

      /*
       * Título "Color, Tamaño"
       */
      .os-vd__tier-variants-title {
        margin-top: 7px !important;
        margin-bottom: 6px !important;

        color: #555555 !important;

        font-size: 12px !important;
        font-weight: 400 !important;
        line-height: 1.25 !important;
      }

      /*
       * Móvil
       */
      @media screen and (max-width: 749px) {
        .sam-competitor-color-select {
          width: 70px !important;
          min-width: 70px !important;
          max-width: 70px !important;
        }

        .sam-competitor-size-select {
          width: 44px !important;
          min-width: 44px !important;
          max-width: 44px !important;
        }

        .sam-competitor-color-select,
        .sam-competitor-size-select {
          height: 32px !important;
          min-height: 32px !important;
          max-height: 32px !important;
          font-size: 14px !important;
        }
      }
    `;

    document.head.appendChild(style);
  }

  /* =========================================================
     OBSERVE ONE STOP RE-RENDERS
  ========================================================= */

  const observer = new MutationObserver(mutations => {
    let relevant = false;

    for (const mutation of mutations) {
      if (mutation.type !== 'childList') continue;

      if (
        mutation.addedNodes.length ||
        mutation.removedNodes.length
      ) {
        relevant = true;
        break;
      }
    }

    if (relevant) {
      scheduleProcess();
    }
  });

  /* =========================================================
     INIT
  ========================================================= */

  function init() {
    injectStyles();
    processAll();

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });

    /*
     * One Stop puede terminar de renderizar después del DOM.
     * Estas pasadas cubren esos renders tardíos.
     */
    setTimeout(processAll, 250);
    setTimeout(processAll, 750);
    setTimeout(processAll, 1500);
    setTimeout(processAll, 3000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, {
      once: true
    });
  } else {
    init();
  }
})();